"use client";

import { useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";

import { JWT_EXPIRED_EVENT_NAME } from "@/data/axios";
import { isCentralizedJwtExpirationEnabled } from "@/data/constants/applicationFeatureFlags";
import { addDatadogAction, clearDatadogUser } from "@/utils/datadog";

/**
 * Single owner of the JWT-expired -> /session-timeout redirect.
 *
 * Listens for the `aipx:jwt-expired` CustomEvent dispatched by
 * `src/data/axios.ts` (proactively when a stored token has an `exp` in
 * the past, reactively when the response interceptor sees a 401 with
 * the backend's "Authorization error:" body shape).
 *
 * - `useRef` flag de-dupes parallel 401s so 50 concurrent failed calls
 *   produce a single redirect.
 * - Pathname guard prevents loops on `/session-timeout` itself (which
 *   calls `signOut` and may trigger more 401s) and on the root `/`
 *   landing page where the user is not yet authenticated.
 * - Internally checks `isCentralizedJwtExpirationEnabled()` so the
 *   component is safe to mount in `layout.tsx` regardless of flag
 *   state. When the flag is OFF the component is a no-op (no listener
 *   registered) and the legacy `handleAppError({ checkJWTExpiration })`
 *   path remains authoritative.
 */
export default function AuthExpirationWatcher(): null {
  const router = useRouter();
  const pathname = usePathname();
  const redirected = useRef(false);

  useEffect(() => {
    if (!isCentralizedJwtExpirationEnabled()) return;
    if (typeof window === "undefined") return;

    const handler = (event: Event) => {
      if (redirected.current) return;
      if (
        pathname &&
        (pathname.startsWith("/session-timeout") || pathname === "/")
      ) {
        return;
      }

      redirected.current = true;
      const reason =
        (event as CustomEvent<{ reason?: string }>).detail?.reason ?? "unknown";

      addDatadogAction("auth_jwt_expired", {
        reason,
        path: pathname ?? "unknown"
      });
      clearDatadogUser();

      router.replace("/session-timeout");
    };

    window.addEventListener(JWT_EXPIRED_EVENT_NAME, handler);
    return () => window.removeEventListener(JWT_EXPIRED_EVENT_NAME, handler);
  }, [router, pathname]);

  return null;
}
